import User from 'classes/User';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { getPermissions } from 'store/Orbita/AccessPolicy/actions';

import { checkPermission } from './checkPermission';

import type IUser from 'interfaces/IUser';
import type { IAccessPolicyPermissionsAction } from 'store/Orbita/AccessPolicy/interfaces';

interface IProps {
  passIfNull?: boolean;
  action?: string | string[];
  inverse?: boolean | number;
  userTypes?: string[];
  currentUser?: IUser;

  onceVerified?: (canAccess: boolean) => void;
}

interface IPropsFromConnect {
  isFetching: boolean;
  permissions: IAccessPolicyPermissionsAction;

  getPermissions?: typeof getPermissions;
}

interface IState {
  canAccess: boolean;
  verified?: boolean;
  callbackCalled?: boolean;
}

class PermissionHide extends PureComponent<IProps & IPropsFromConnect, IState> {
  static getDerivedStateFromProps(props: IProps & IPropsFromConnect, state: IState): IState {
    if (props.isFetching) {
      return { ...state, canAccess: false, verified: false };
    }

    if (props.passIfNull && !props.action) {
      return { ...state, canAccess: true, verified: true };
    }

    return {
      ...state,
      verified: true,
      canAccess: checkPermission(props.permissions, props.action),
    };
  }

  constructor(props: IProps & IPropsFromConnect) {
    super(props);
    this.props.getPermissions();
    this.state = { canAccess: false, verified: false, callbackCalled: false };
  }

  componentDidMount() {
    this.callCallback();
  }

  componentDidUpdate() {
    this.callCallback();
  }

  callCallback() {
    if (this.props.onceVerified && this.state.verified && !this.state.callbackCalled) {
      this.props.onceVerified(this.state.canAccess);
      this.setState({ callbackCalled: true });
    }
  }

  userCanViewByType(userTypes: string[], currentUser: IUser) {
    if (!Array.isArray(userTypes)) {
      return true;
    }

    const isProducer = currentUser.isProducer();
    const isAffiliate = currentUser.isAffiliate();
    const isCustomer = currentUser.isCustomer();
    const isPartner = currentUser.general_term;
    const isOnlyCustomer = !currentUser.producer && !currentUser.affiliate && !isPartner;
    const isOnlyProducer = currentUser.producer && !currentUser.affiliate;
    const isOnlyAffiliate = !currentUser.producer && currentUser.affiliate;

    if (userTypes.includes('producer') && isPartner) return true;
    if (userTypes.includes('affiliate') && isPartner) return true;
    if (userTypes.includes('producer') && isPartner) return true;
    if (userTypes.includes('onlyProducer') && isPartner) return true;
    if (userTypes.includes('onlyAffiliate') && isPartner) return true;
    if (userTypes.includes('producer') && isProducer) return true;
    if (userTypes.includes('affiliate') && isAffiliate) return true;
    if (userTypes.includes('customer') && isCustomer) return true;
    if (userTypes.includes('onlyCustomer') && isOnlyCustomer) return true;
    if (userTypes.includes('onlyProducer') && isOnlyProducer) return true;
    if (userTypes.includes('onlyAffiliate') && isOnlyAffiliate) return true;

    return false;
  }

  render() {
    const { canAccess, verified } = this.state;
    const { inverse, permissions, userTypes, currentUser } = this.props;

    if (!verified) {
      return null;
    }

    if (inverse === 1 && canAccess && permissions.owned) {
      return this.props.children;
    }

    if (inverse === false && canAccess && permissions.owned) {
      return null;
    }

    if (inverse === false && canAccess) {
      return this.props.children;
    }

    if (inverse && !canAccess) {
      return this.props.children;
    }

    if (inverse || !canAccess) {
      return null;
    }

    if (!this.userCanViewByType(userTypes, currentUser)) {
      return false;
    }

    return this.props.children;
  }
}

function mapStateToProps(state: any) {
  return {
    isFetching: state.orbita.AccessPolicy.permissions.isFetching,
    permissions: state.orbita.AccessPolicy.permissions.data,
    currentUser: new User(state.orbita.User.user),
  };
}

export default connect<IPropsFromConnect, {}, IProps>(mapStateToProps, {
  getPermissions,
})(PermissionHide);
